import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { fetchWithCsrf } from "./fetchWithCsrf";
import { resolveApiUrl } from "./resolveApiUrl";

type PlatformProject = {
  deployMode: "git" | "compose";
  composeYaml: string | null;
  activeEnvironment: string;
  prPreviewEnabled: boolean;
  skewProtectionEnabled: boolean;
  healthCheckPath: string;
  customStartCommand: string | null;
  cdnAssetBaseUrl: string | null;
  redirectsJson: Array<{ source: string; destination: string; permanent?: boolean }> | null;
  headersJson: Array<{ source: string; headers: Record<string, string> }> | null;
};

type DeployHook = {
  id: string;
  name: string;
  enabled: boolean;
  hookUrl: string;
  createdAt: string;
};

type DeployWebhook = {
  id: string;
  url: string;
  events: Array<"deployment.success" | "deployment.failed">;
  enabled: boolean;
  hasSecret: boolean;
  createdAt: string;
};

type Volume = {
  id: string;
  name: string;
  mountPath: string;
  dockerVolumeName: string;
  createdAt: string;
};

type CronJob = {
  id: string;
  name: string;
  schedule: string;
  command: string;
  enabled: boolean;
  lastRunAt: string | null;
  createdAt: string;
};

type ManagedService = {
  id: string;
  serviceType: "postgres" | "redis" | "mysql";
  name: string;
  containerName: string;
  connectionEnvKey: string;
  status: string;
  createdAt: string;
};

type StorageObject = {
  id: string;
  objectKey: string;
  contentType: string | null;
  sizeBytes: number;
  createdAt: string;
};

type Toast = (message: string, variant: "success" | "error" | "warning") => void;

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : fallback;
};

const selectClass =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs";

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes)) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const jsonText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
};

export const ProjectPlatformPanel = ({
  projectId,
  project,
  onToast,
  onRequestSettingsRefetch
}: {
  projectId: string;
  project: PlatformProject;
  onToast: Toast;
  onRequestSettingsRefetch?: () => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [deployMode, setDeployMode] = React.useState(project.deployMode);
  const [composeYaml, setComposeYaml] = React.useState(project.composeYaml ?? "");
  const [activeEnvironment, setActiveEnvironment] = React.useState(project.activeEnvironment);
  const [prPreviewEnabled, setPrPreviewEnabled] = React.useState(project.prPreviewEnabled);
  const [skewProtectionEnabled, setSkewProtectionEnabled] = React.useState(project.skewProtectionEnabled);
  const [healthCheckPath, setHealthCheckPath] = React.useState(project.healthCheckPath);
  const [customStartCommand, setCustomStartCommand] = React.useState(project.customStartCommand ?? "");
  const [cdnAssetBaseUrl, setCdnAssetBaseUrl] = React.useState(project.cdnAssetBaseUrl ?? "");
  const [redirectsJson, setRedirectsJson] = React.useState(jsonText(project.redirectsJson));
  const [headersJson, setHeadersJson] = React.useState(jsonText(project.headersJson));
  const [savingSettings, setSavingSettings] = React.useState(false);

  const [hooks, setHooks] = React.useState<DeployHook[]>([]);
  const [hookName, setHookName] = React.useState("");
  const [newHookSecret, setNewHookSecret] = React.useState<string | null>(null);
  const [hooksLoading, setHooksLoading] = React.useState(true);

  const [webhooks, setWebhooks] = React.useState<DeployWebhook[]>([]);
  const [webhookUrl, setWebhookUrl] = React.useState("");
  const [webhookSecret, setWebhookSecret] = React.useState("");
  const [webhooksLoading, setWebhooksLoading] = React.useState(true);

  const [volumes, setVolumes] = React.useState<Volume[]>([]);
  const [volumeName, setVolumeName] = React.useState("");
  const [volumeMountPath, setVolumeMountPath] = React.useState("");
  const [volumesLoading, setVolumesLoading] = React.useState(true);

  const [cronJobs, setCronJobs] = React.useState<CronJob[]>([]);
  const [cronName, setCronName] = React.useState("");
  const [cronSchedule, setCronSchedule] = React.useState("");
  const [cronCommand, setCronCommand] = React.useState("");
  const [cronLoading, setCronLoading] = React.useState(true);

  const [services, setServices] = React.useState<ManagedService[]>([]);
  const [serviceType, setServiceType] = React.useState<"postgres" | "redis" | "mysql">("postgres");
  const [serviceName, setServiceName] = React.useState("");
  const [servicesLoading, setServicesLoading] = React.useState(true);

  const [objects, setObjects] = React.useState<StorageObject[]>([]);
  const [objectKey, setObjectKey] = React.useState("");
  const [storageLoading, setStorageLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);

  const [vercelJson, setVercelJson] = React.useState("");
  const [deployherToml, setDeployherToml] = React.useState("");
  const [migrating, setMigrating] = React.useState(false);
  const [copied, setCopied] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDeployMode(project.deployMode);
    setComposeYaml(project.composeYaml ?? "");
    setActiveEnvironment(project.activeEnvironment);
    setPrPreviewEnabled(project.prPreviewEnabled);
    setSkewProtectionEnabled(project.skewProtectionEnabled);
    setHealthCheckPath(project.healthCheckPath);
    setCustomStartCommand(project.customStartCommand ?? "");
    setCdnAssetBaseUrl(project.cdnAssetBaseUrl ?? "");
    setRedirectsJson(jsonText(project.redirectsJson));
    setHeadersJson(jsonText(project.headersJson));
  }, [project]);

  const loadHooks = React.useCallback(async (): Promise<void> => {
    setHooksLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/deploy-hooks`), {
        credentials: "include"
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.hooks.loadFailed")));
      setHooks((await response.json()) as DeployHook[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.hooks.loadFailed"), "error");
    } finally {
      setHooksLoading(false);
    }
  }, [onToast, projectId, t]);

  const loadWebhooks = React.useCallback(async (): Promise<void> => {
    setWebhooksLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/deploy-webhooks`), {
        credentials: "include"
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.webhooks.loadFailed")));
      setWebhooks((await response.json()) as DeployWebhook[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.webhooks.loadFailed"), "error");
    } finally {
      setWebhooksLoading(false);
    }
  }, [onToast, projectId, t]);

  const loadVolumes = React.useCallback(async (): Promise<void> => {
    setVolumesLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/volumes`), { credentials: "include" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.volumes.loadFailed")));
      setVolumes((await response.json()) as Volume[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.volumes.loadFailed"), "error");
    } finally {
      setVolumesLoading(false);
    }
  }, [onToast, projectId, t]);

  const loadCronJobs = React.useCallback(async (): Promise<void> => {
    setCronLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/cron-jobs`), { credentials: "include" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.cron.loadFailed")));
      setCronJobs((await response.json()) as CronJob[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.cron.loadFailed"), "error");
    } finally {
      setCronLoading(false);
    }
  }, [onToast, projectId, t]);

  const loadServices = React.useCallback(async (): Promise<void> => {
    setServicesLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/managed-services`), {
        credentials: "include"
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.services.loadFailed")));
      setServices((await response.json()) as ManagedService[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.services.loadFailed"), "error");
    } finally {
      setServicesLoading(false);
    }
  }, [onToast, projectId, t]);

  const loadStorage = React.useCallback(async (): Promise<void> => {
    setStorageLoading(true);
    try {
      const response = await fetch(resolveApiUrl(`/api/projects/${projectId}/storage`), { credentials: "include" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.storage.loadFailed")));
      setObjects((await response.json()) as StorageObject[]);
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.storage.loadFailed"), "error");
    } finally {
      setStorageLoading(false);
    }
  }, [onToast, projectId, t]);

  React.useEffect(() => {
    void loadHooks();
    void loadWebhooks();
    void loadVolumes();
    void loadCronJobs();
    void loadServices();
    void loadStorage();
  }, [loadHooks, loadWebhooks, loadVolumes, loadCronJobs, loadServices, loadStorage]);

  const copyText = async (key: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => (current === key ? null : current)), 1200);
    } catch {
      onToast(t("projectSettings.platform.copyFailed"), "warning");
    }
  };

  const saveSettings = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSavingSettings(true);
    try {
      let parsedRedirects: unknown = null;
      let parsedHeaders: unknown = null;
      if (redirectsJson.trim()) {
        parsedRedirects = JSON.parse(redirectsJson) as unknown;
      }
      if (headersJson.trim()) {
        parsedHeaders = JSON.parse(headersJson) as unknown;
      }

      const body = {
        deployMode,
        composeYaml: composeYaml.trim() ? composeYaml : null,
        activeEnvironment,
        prPreviewEnabled,
        skewProtectionEnabled,
        healthCheckPath: healthCheckPath.trim(),
        customStartCommand: customStartCommand.trim() ? customStartCommand : null,
        cdnAssetBaseUrl: cdnAssetBaseUrl.trim() ? cdnAssetBaseUrl.trim() : null,
        redirectsJson: parsedRedirects,
        headersJson: parsedHeaders
      };

      const response = await fetchWithCsrf(`/api/projects/${projectId}/platform`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.settings.saveFailed")));
      onToast(t("projectSettings.platform.settings.saved"), "success");
      onRequestSettingsRefetch?.();
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.settings.saveFailed"), "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const createHook = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!hookName.trim()) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/deploy-hooks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: hookName.trim() })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.hooks.createFailed")));
      const created = (await response.json()) as DeployHook & { secret?: string };
      setHookName("");
      setNewHookSecret(created.secret ?? null);
      await loadHooks();
      onToast(t("projectSettings.platform.hooks.created"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.hooks.createFailed"), "error");
    }
  };

  const deleteHook = async (hookId: string): Promise<void> => {
    if (!window.confirm(t("projectSettings.platform.hooks.deleteConfirm"))) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/deploy-hooks/${hookId}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.hooks.deleteFailed")));
      await loadHooks();
      onToast(t("projectSettings.platform.hooks.deleted"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.hooks.deleteFailed"), "error");
    }
  };

  const createWebhook = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!webhookUrl.trim()) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/deploy-webhooks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: webhookUrl.trim(),
          secret: webhookSecret.trim() || undefined,
          events: ["deployment.success", "deployment.failed"]
        })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.webhooks.createFailed")));
      setWebhookUrl("");
      setWebhookSecret("");
      await loadWebhooks();
      onToast(t("projectSettings.platform.webhooks.created"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.webhooks.createFailed"), "error");
    }
  };

  const deleteWebhook = async (webhookId: string): Promise<void> => {
    if (!window.confirm(t("projectSettings.platform.webhooks.deleteConfirm"))) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/deploy-webhooks/${webhookId}`, {
        method: "DELETE"
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.webhooks.deleteFailed")));
      await loadWebhooks();
      onToast(t("projectSettings.platform.webhooks.deleted"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.webhooks.deleteFailed"), "error");
    }
  };

  const createVolume = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/volumes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: volumeName.trim(), mountPath: volumeMountPath.trim() })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.volumes.createFailed")));
      setVolumeName("");
      setVolumeMountPath("");
      await loadVolumes();
      onToast(t("projectSettings.platform.volumes.created"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.volumes.createFailed"), "error");
    }
  };

  const deleteVolume = async (volumeId: string): Promise<void> => {
    if (!window.confirm(t("projectSettings.platform.volumes.deleteConfirm"))) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/volumes/${volumeId}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.volumes.deleteFailed")));
      await loadVolumes();
      onToast(t("projectSettings.platform.volumes.deleted"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.volumes.deleteFailed"), "error");
    }
  };

  const createCronJob = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/cron-jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: cronName.trim(),
          schedule: cronSchedule.trim(),
          command: cronCommand.trim()
        })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.cron.createFailed")));
      setCronName("");
      setCronSchedule("");
      setCronCommand("");
      await loadCronJobs();
      onToast(t("projectSettings.platform.cron.created"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.cron.createFailed"), "error");
    }
  };

  const deleteCronJob = async (cronJobId: string): Promise<void> => {
    if (!window.confirm(t("projectSettings.platform.cron.deleteConfirm"))) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/cron-jobs/${cronJobId}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.cron.deleteFailed")));
      await loadCronJobs();
      onToast(t("projectSettings.platform.cron.deleted"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.cron.deleteFailed"), "error");
    }
  };

  const createService = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/managed-services`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: serviceType, name: serviceName.trim() })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.services.createFailed")));
      setServiceName("");
      await loadServices();
      onToast(t("projectSettings.platform.services.created"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.services.createFailed"), "error");
    }
  };

  const deleteService = async (serviceId: string): Promise<void> => {
    if (!window.confirm(t("projectSettings.platform.services.deleteConfirm"))) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/managed-services/${serviceId}`, {
        method: "DELETE"
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.services.deleteFailed")));
      await loadServices();
      onToast(t("projectSettings.platform.services.deleted"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.services.deleteFailed"), "error");
    }
  };

  const uploadObject = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      const form = new FormData();
      form.set("file", file);
      if (objectKey.trim()) form.set("objectKey", objectKey.trim());
      const response = await fetchWithCsrf(`/api/projects/${projectId}/storage`, {
        method: "POST",
        body: form
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.storage.uploadFailed")));
      setObjectKey("");
      await loadStorage();
      onToast(t("projectSettings.platform.storage.uploaded"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.storage.uploadFailed"), "error");
    } finally {
      setUploading(false);
    }
  };

  const deleteObject = async (objectId: string): Promise<void> => {
    if (!window.confirm(t("projectSettings.platform.storage.deleteConfirm"))) return;
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/storage/${objectId}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.storage.deleteFailed")));
      await loadStorage();
      onToast(t("projectSettings.platform.storage.deleted"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.storage.deleteFailed"), "error");
    }
  };

  const migrateVercel = async (): Promise<void> => {
    if (!vercelJson.trim()) return;
    setMigrating(true);
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/migrate-vercel-json`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: vercelJson })
      });
      if (!response.ok) throw new Error(await readError(response, t("projectSettings.platform.migrator.failed")));
      const body = (await response.json()) as { deployherToml?: string };
      setDeployherToml(body.deployherToml ?? "");
      onToast(t("projectSettings.platform.migrator.success"), "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : t("projectSettings.platform.migrator.failed"), "error");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold">{t("projectSettings.platform.heading")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("projectSettings.platform.intro")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.settings.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.settings.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(event) => void saveSettings(event)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="platform-deploy-mode">{t("projectSettings.platform.settings.deployMode")}</Label>
                <select
                  id="platform-deploy-mode"
                  className={selectClass}
                  value={deployMode}
                  onChange={(event) => setDeployMode(event.target.value as "git" | "compose")}
                >
                  <option value="git">{t("projectSettings.platform.settings.deployModeGit")}</option>
                  <option value="compose">{t("projectSettings.platform.settings.deployModeCompose")}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="platform-active-env">{t("projectSettings.platform.settings.activeEnvironment")}</Label>
                <select
                  id="platform-active-env"
                  className={selectClass}
                  value={activeEnvironment}
                  onChange={(event) => setActiveEnvironment(event.target.value)}
                >
                  <option value="production">{t("projectSettings.platform.settings.envProduction")}</option>
                  <option value="staging">{t("projectSettings.platform.settings.envStaging")}</option>
                  <option value="preview">{t("projectSettings.platform.settings.envPreview")}</option>
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="platform-compose-yaml">{t("projectSettings.platform.settings.composeYaml")}</Label>
              <Textarea
                id="platform-compose-yaml"
                rows={6}
                value={composeYaml}
                onChange={(event) => setComposeYaml(event.target.value)}
                className="font-mono text-xs"
                placeholder="services: ..."
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={prPreviewEnabled}
                  onChange={(event) => setPrPreviewEnabled(event.target.checked)}
                />
                {t("projectSettings.platform.settings.prPreviewEnabled")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skewProtectionEnabled}
                  onChange={(event) => setSkewProtectionEnabled(event.target.checked)}
                />
                {t("projectSettings.platform.settings.skewProtectionEnabled")}
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="platform-health-path">{t("projectSettings.platform.settings.healthCheckPath")}</Label>
                <Input
                  id="platform-health-path"
                  value={healthCheckPath}
                  onChange={(event) => setHealthCheckPath(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="platform-cdn-url">{t("projectSettings.platform.settings.cdnAssetBaseUrl")}</Label>
                <Input
                  id="platform-cdn-url"
                  value={cdnAssetBaseUrl}
                  onChange={(event) => setCdnAssetBaseUrl(event.target.value)}
                  placeholder="https://cdn.example.com"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="platform-start-command">{t("projectSettings.platform.settings.customStartCommand")}</Label>
              <Input
                id="platform-start-command"
                value={customStartCommand}
                onChange={(event) => setCustomStartCommand(event.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="platform-redirects">{t("projectSettings.platform.settings.redirectsJson")}</Label>
              <Textarea
                id="platform-redirects"
                rows={4}
                value={redirectsJson}
                onChange={(event) => setRedirectsJson(event.target.value)}
                className="font-mono text-xs"
                placeholder='[{"source":"/old","destination":"/new","permanent":true}]'
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="platform-headers">{t("projectSettings.platform.settings.headersJson")}</Label>
              <Textarea
                id="platform-headers"
                rows={4}
                value={headersJson}
                onChange={(event) => setHeadersJson(event.target.value)}
                className="font-mono text-xs"
                placeholder='[{"source":"/api/*","headers":{"X-Frame-Options":"DENY"}}]'
              />
            </div>

            <Button type="submit" disabled={savingSettings}>
              {savingSettings ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {t("projectSettings.platform.settings.save")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.hooks.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.hooks.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {newHookSecret ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <p className="font-medium">{t("projectSettings.platform.hooks.secretOnce")}</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-background px-2 py-1 font-mono text-xs">{newHookSecret}</code>
                <Button type="button" size="sm" variant="outline" onClick={() => void copyText("hook-secret", newHookSecret)}>
                  {copied === "hook-secret" ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </div>
          ) : null}
          <form className="flex flex-col gap-3 sm:flex-row" onSubmit={(event) => void createHook(event)}>
            <Input
              value={hookName}
              onChange={(event) => setHookName(event.target.value)}
              placeholder={t("projectSettings.platform.hooks.namePlaceholder")}
            />
            <Button type="submit">
              <Plus className="size-4" aria-hidden />
              {t("projectSettings.platform.hooks.create")}
            </Button>
          </form>
          {hooksLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : hooks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectSettings.platform.hooks.empty")}</p>
          ) : (
            <div className="space-y-2">
              {hooks.map((hook) => (
                <div key={hook.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{hook.name}</p>
                    <p className="truncate font-mono text-xs text-muted-foreground">
                      {resolveApiUrl(hook.hookUrl)} · {t("projectSettings.platform.hooks.secretHeader")}: x-deployher-hook-secret
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void deleteHook(hook.id)}>
                    <Trash2 className="size-4" aria-hidden />
                    {t("projectSettings.platform.hooks.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.webhooks.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.webhooks.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => void createWebhook(event)}>
            <Input
              value={webhookUrl}
              onChange={(event) => setWebhookUrl(event.target.value)}
              placeholder="https://example.com/webhooks/deploy"
            />
            <Input
              value={webhookSecret}
              onChange={(event) => setWebhookSecret(event.target.value)}
              placeholder={t("projectSettings.platform.webhooks.secretOptional")}
            />
            <div className="sm:col-span-2">
              <Button type="submit">
                <Plus className="size-4" aria-hidden />
                {t("projectSettings.platform.webhooks.create")}
              </Button>
            </div>
          </form>
          {webhooksLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : webhooks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectSettings.platform.webhooks.empty")}</p>
          ) : (
            <div className="space-y-2">
              {webhooks.map((webhook) => (
                <div key={webhook.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{webhook.url}</p>
                    <p className="text-xs text-muted-foreground">
                      {webhook.events.join(", ")}
                      {webhook.hasSecret ? ` · ${t("projectSettings.platform.webhooks.hasSecret")}` : ""}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void deleteWebhook(webhook.id)}>
                    <Trash2 className="size-4" aria-hidden />
                    {t("projectSettings.platform.webhooks.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.volumes.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.volumes.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="grid gap-3 sm:grid-cols-3" onSubmit={(event) => void createVolume(event)}>
            <Input value={volumeName} onChange={(event) => setVolumeName(event.target.value)} placeholder={t("projectSettings.platform.volumes.name")} />
            <Input value={volumeMountPath} onChange={(event) => setVolumeMountPath(event.target.value)} placeholder="/data" />
            <Button type="submit">
              <Plus className="size-4" aria-hidden />
              {t("projectSettings.platform.volumes.create")}
            </Button>
          </form>
          {volumesLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : volumes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectSettings.platform.volumes.empty")}</p>
          ) : (
            <div className="space-y-2">
              {volumes.map((volume) => (
                <div key={volume.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{volume.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {volume.mountPath} · {volume.dockerVolumeName}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void deleteVolume(volume.id)}>
                    <Trash2 className="size-4" aria-hidden />
                    {t("projectSettings.platform.volumes.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.cron.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.cron.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => void createCronJob(event)}>
            <Input value={cronName} onChange={(event) => setCronName(event.target.value)} placeholder={t("projectSettings.platform.cron.name")} />
            <Input value={cronSchedule} onChange={(event) => setCronSchedule(event.target.value)} placeholder="0 * * * *" />
            <Input
              className="sm:col-span-2"
              value={cronCommand}
              onChange={(event) => setCronCommand(event.target.value)}
              placeholder={t("projectSettings.platform.cron.commandPlaceholder")}
            />
            <Button type="submit">
              <Plus className="size-4" aria-hidden />
              {t("projectSettings.platform.cron.create")}
            </Button>
          </form>
          {cronLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : cronJobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectSettings.platform.cron.empty")}</p>
          ) : (
            <div className="space-y-2">
              {cronJobs.map((job) => (
                <div key={job.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{job.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {job.schedule} · {job.command}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void deleteCronJob(job.id)}>
                    <Trash2 className="size-4" aria-hidden />
                    {t("projectSettings.platform.cron.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.services.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.services.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="grid gap-3 sm:grid-cols-3" onSubmit={(event) => void createService(event)}>
            <select className={selectClass} value={serviceType} onChange={(event) => setServiceType(event.target.value as "postgres" | "redis" | "mysql")}>
              <option value="postgres">Postgres</option>
              <option value="redis">Redis</option>
              <option value="mysql">MySQL</option>
            </select>
            <Input value={serviceName} onChange={(event) => setServiceName(event.target.value)} placeholder={t("projectSettings.platform.services.name")} />
            <Button type="submit">
              <Plus className="size-4" aria-hidden />
              {t("projectSettings.platform.services.create")}
            </Button>
          </form>
          {servicesLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : services.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectSettings.platform.services.empty")}</p>
          ) : (
            <div className="space-y-2">
              {services.map((service) => (
                <div key={service.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {service.name} <span className="text-muted-foreground">({service.serviceType})</span>
                    </p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {service.containerName} · {service.connectionEnvKey} · {service.status}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void deleteService(service.id)}>
                    <Trash2 className="size-4" aria-hidden />
                    {t("projectSettings.platform.services.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.storage.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.storage.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="platform-object-key">{t("projectSettings.platform.storage.objectKeyOptional")}</Label>
              <Input id="platform-object-key" value={objectKey} onChange={(event) => setObjectKey(event.target.value)} />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void uploadObject(file);
              }}
            />
            <Button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Upload className="size-4" aria-hidden />}
              {t("projectSettings.platform.storage.upload")}
            </Button>
          </div>
          {storageLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : objects.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectSettings.platform.storage.empty")}</p>
          ) : (
            <div className="space-y-2">
              {objects.map((object) => (
                <div key={object.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{object.objectKey}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(object.sizeBytes)}
                      {object.contentType ? ` · ${object.contentType}` : ""}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => void deleteObject(object.id)}>
                    <Trash2 className="size-4" aria-hidden />
                    {t("projectSettings.platform.storage.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("projectSettings.platform.migrator.title")}</CardTitle>
          <CardDescription>{t("projectSettings.platform.migrator.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="platform-vercel-json">{t("projectSettings.platform.migrator.vercelJson")}</Label>
            <Textarea
              id="platform-vercel-json"
              rows={8}
              value={vercelJson}
              onChange={(event) => setVercelJson(event.target.value)}
              className="font-mono text-xs"
            />
          </div>
          <Button type="button" disabled={migrating || !vercelJson.trim()} onClick={() => void migrateVercel()}>
            {migrating ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {t("projectSettings.platform.migrator.convert")}
          </Button>
          {deployherToml ? (
            <>
              <Separator />
              <div className="space-y-1.5">
                <Label htmlFor="platform-deployher-toml">{t("projectSettings.platform.migrator.deployherToml")}</Label>
                <Textarea
                  id="platform-deployher-toml"
                  rows={10}
                  readOnly
                  value={deployherToml}
                  className={cn("font-mono text-xs")}
                />
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
